/* ============================================================
   PST Pool Customizer — the state engine behind the four-step build.

   Deliberately thinner than assets/pst-pool-kit.js (which drives the
   pool-builder view and is untouched by this file). Two ideas keep it small:

   1. The DOM is the single source of truth. Every choice is a real radio or
      checkbox, so "what has been picked" is just "what is checked" — there is
      no parallel object to drift out of sync. sessionStorage only remembers
      which ids were checked so a returning shopper resumes where they left off.

   2. Quantity is fixed at one. First-time buyers do not need a stepper on a
      pool ladder; anyone who wants two can change it in the cart. Removing
      quantity removes a whole class of UI.

   The finished package still goes to Shopify as a single /cart/add.js items[]
   POST, then hands off to the theme's own cart drawer.
   ============================================================ */
(function () {
  'use strict';

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function money(cents, currency) {
    var amount = (cents || 0) / 100;
    try {
      // Always two decimals: these totals sit directly above rows rendered by
      // Liquid's `money` filter, and dropping the cents on round numbers makes
      // the two disagree in the same column.
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: 2
      }).format(amount);
    } catch (e) {
      return '$' + Math.round(amount).toLocaleString();
    }
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  class PstPoolCustomizer extends HTMLElement {
    connectedCallback() {
      if (this._booted) return;
      this._booted = true;

      this.currency = this.getAttribute('data-currency') || 'USD';
      this.pool = {
        variantId: this.getAttribute('data-pool-variant'),
        price: parseInt(this.getAttribute('data-pool-price') || '0', 10),
        title: this.getAttribute('data-pool-title') || 'Pool',
        image: this.getAttribute('data-pool-image') || '',
        thumb: this.getAttribute('data-pool-thumb') || ''
      };
      this.defaultPattern = this.getAttribute('data-default-liner-pattern') || '';
      // FREE Liner program: the pool's price includes its included liner, liner rows carry
      // data-price = what the shopper pays for them ($0 included, the difference for other
      // patterns), and a liner is required — there is no "I already have a liner" card.
      this.freeLiner = this.getAttribute('data-free-liner') === 'true';

      this.steps = Array.prototype.slice.call(this.querySelectorAll('[data-cx-step]'));
      if (!this.steps.length) return;

      this.stageFrame = this.querySelector('[data-cx-stage-frame]');
      this.poolStageHTML = this.stageFrame ? this.stageFrame.innerHTML : '';

      this.indexOptions();
      this.buildNav();
      this.bind();
      this.watchChrome();

      var saved = this.restore();
      // A session saved as "own liner" has nothing to restore once the opt-out is gone.
      if ((!saved || (this.freeLiner && !this.patternValue())) && this.defaultPattern) this.preselectLiner(this.defaultPattern);

      this.syncLinerFits();
      this.fitStage();
      this.go(saved ? saved.step : 0, false);
      this.refresh();

      // Open with the builder filling the screen, the site header already scrolled away (the
      // first Next used to be what did that). Again on load: late fonts and images can move it.
      var self = this;
      this.clearHeader(true);
      window.addEventListener('load', function () { self.clearHeader(true); }, { once: true });
    }

    /* The sticky stage has to start exactly where the bar ends, and the bar is
       not a fixed height: it wraps the step tabs onto a second row on narrow
       screens, and a long pool name can push it further. Measuring it beats
       guessing — with the height hardcoded the stage slid underneath the bar on
       every phone. The footer is measured for the same reason.

       The measurement is written to its OWN variable rather than back into
       --cx-bar-h: that one sets the bar's min-height, so feeding a measurement
       into it makes the bar depend on its own size and ResizeObserver breaks
       the cycle by dropping the notification — leaving a stale value behind. */
    watchChrome() {
      var self = this;
      var bar = this.querySelector('.pst-cx__bar');
      var foot = this.querySelector('.pst-cx__foot');
      var measure = function () {
        if (bar) self.style.setProperty('--cx-bar-real', Math.ceil(bar.getBoundingClientRect().height) + 'px');
        if (foot) {
          var h = Math.ceil(foot.getBoundingClientRect().height);
          self.style.setProperty('--cx-foot-real', h + 'px');
          // Shopify Inbox's launcher lives outside this component, so the
          // height it has to clear has to be published somewhere global.
          document.documentElement.style.setProperty('--cx-chat-lift', h + 'px');
        }
      };
      measure();
      if (window.ResizeObserver) {
        var ro = new ResizeObserver(measure);
        if (bar) ro.observe(bar);
        if (foot) ro.observe(foot);
      } else {
        window.addEventListener('resize', measure);
      }
    }

    /* ---- give each option a stagger index so a step reveals as one gesture ---- */
    indexOptions() {
      Array.prototype.forEach.call(this.querySelectorAll('.pst-cx-grp__grid'), function (grid) {
        Array.prototype.forEach.call(grid.children, function (child, i) {
          child.style.setProperty('--cx-i', String(Math.min(i, 12)));
        });
      });
    }

    /* ============================================================
       Persistence — only the ids, resolved back against the DOM
       ============================================================ */
    key() { return 'pstcx:' + (this.getAttribute('data-pool-handle') || 'pool'); }

    persist() {
      var checked = [];
      this.options().forEach(function (el) {
        var input = el.querySelector('.pst-cx-opt__input');
        if (input && input.checked && el.getAttribute('data-id')) checked.push(el.getAttribute('data-id'));
      });
      var liner = this.linerEl();
      try {
        sessionStorage.setItem(this.key(), JSON.stringify({
          step: this.step || 0,
          ids: checked,
          liner: liner ? liner.getAttribute('data-id') : null,
          pattern: this.patternValue(),
          ownLiner: this.ownsLiner()
        }));
      } catch (e) {}
    }

    restore() {
      var data = null;
      try { data = JSON.parse(sessionStorage.getItem(this.key())); } catch (e) {}
      if (!data) return null;

      var self = this;
      (data.ids || []).forEach(function (id) {
        var el = self.querySelector('[data-cx-opt][data-id="' + CSS.escape(id) + '"]');
        var input = el && el.querySelector('.pst-cx-opt__input');
        if (input && !input.disabled) input.checked = true;
      });
      if (data.ownLiner) {
        var own = this.querySelector('[data-cx-pattern-none] .pst-cx-opt__input');
        if (own) own.checked = true;
      } else if (data.pattern) {
        var sw = this.querySelector('[data-cx-pattern="' + CSS.escape(data.pattern) + '"] .pst-cx-opt__input');
        if (sw) sw.checked = true;
      }
      if (data.liner) {
        var fit = this.querySelector('[data-cx-liner][data-id="' + CSS.escape(data.liner) + '"] .pst-cx-opt__input');
        if (fit) fit.checked = true;
      }
      data.step = Math.max(0, Math.min(this.steps.length - 1, parseInt(data.step, 10) || 0));
      return data;
    }

    preselectLiner(pattern) {
      var sw = this.querySelector('[data-cx-pattern="' + CSS.escape(pattern) + '"] .pst-cx-opt__input');
      if (sw) sw.checked = true;
    }

    /* ============================================================
       Reading the build straight out of the DOM
       ============================================================ */
    options() { return Array.prototype.slice.call(this.querySelectorAll('[data-cx-opt]')); }

    ownsLiner() {
      return !!this.querySelector('[data-cx-pattern-none] .pst-cx-opt__input:checked');
    }

    patternValue() {
      var el = this.querySelector('[data-cx-pattern] .pst-cx-opt__input:checked');
      return el ? el.value : '';
    }

    /* Every pattern keeps its own fit radios, and switching pattern leaves the
       old pattern's row checked (different radio name). So the lookup has to
       start from the chosen pattern's block — asking the document for "the
       checked fit row" hands back whichever comes first in source order, which
       is a different pattern's leftover. */
    linerEl() {
      var pattern = this.patternValue();
      if (!pattern) return null;
      var block = this.querySelector('[data-cx-fit="' + CSS.escape(pattern) + '"]');
      if (!block) return null;
      var input = block.querySelector('[data-cx-liner] .pst-cx-opt__input:checked');
      return input ? input.closest('[data-cx-liner]') : null;
    }

    read(el) {
      return {
        el: el,
        id: el.getAttribute('data-id'),
        variantId: el.getAttribute('data-variant'),
        price: parseInt(el.getAttribute('data-price') || '0', 10),
        qty: Math.max(1, parseInt(el.getAttribute('data-qty') || '1', 10) || 1),
        title: el.getAttribute('data-title') || '',
        image: el.getAttribute('data-image') || '',
        thumb: el.getAttribute('data-thumb') || '',
        group: el.getAttribute('data-group') || el.getAttribute('data-cx-group') || '',
        bucket: el.getAttribute('data-bucket') || ''
      };
    }

    picks() {
      var self = this;
      return this.options()
        .filter(function (el) {
          if (el.getAttribute('data-cx-none') === 'true') return false;
          var input = el.querySelector('.pst-cx-opt__input');
          return input && input.checked && el.getAttribute('data-variant');
        })
        .map(function (el) { return self.read(el); });
    }

    liner() {
      var el = this.linerEl();
      if (!el) return null;
      var d = this.read(el);
      d.summary = el.getAttribute('data-summary') || d.title;
      return d;
    }

    total() {
      var sum = this.pool.price;
      var l = this.liner();
      if (l) sum += l.price;
      // Quantity is per-pool for pieces like the foam cove (a 30ft round needs
      // 24 strips), so the line is unit price x qty, not the unit price.
      this.picks().forEach(function (p) { sum += p.price * p.qty; });
      return sum;
    }

    count() { return 1 + (this.liner() ? 1 : 0) + this.picks().length; }

    /* ============================================================
       Events
       ============================================================ */
    bind() {
      var self = this;

      this.addEventListener('change', function (e) {
        var input = e.target.closest('.pst-cx-opt__input');
        if (!input) return;

        var swatch = input.closest('[data-cx-pattern], [data-cx-pattern-none]');
        if (swatch) {
          self.syncLinerFits();
          self.stageFromLiner(swatch);
        } else {
          var opt = input.closest('[data-cx-opt], [data-cx-liner]');
          if (opt && input.checked) self.stageFrom(opt);
        }
        self.refresh();
        self.persist();
      });

      this.addEventListener('click', function (e) {
        var tab = e.target.closest('[data-cx-tab]');
        if (tab) { self.go(parseInt(tab.getAttribute('data-cx-tab'), 10), true); return; }

        if (e.target.closest('[data-cx-next]')) { self.next(); return; }
        if (e.target.closest('[data-cx-prev]')) { self.go(self.step - 1, true); return; }

        var jump = e.target.closest('[data-cx-goto]');
        if (jump) { self.goKey(jump.getAttribute('data-cx-goto')); return; }

        var drop = e.target.closest('[data-cx-remove]');
        if (drop) {
          // This listener is deeper than the document one, so it runs first —
          // the flag is set here as well as on pointerdown so a synthetic
          // .click() (tests, assistive tech) keeps the panel open too.
          if (drop.closest('[data-cx-totalpanel]')) self._fromTotal = true;
          self.removeItem(drop.getAttribute('data-cx-remove'));
          return;
        }

        if (e.target.closest('[data-cx-totaltoggle]')) { self.toggleTotal(); return; }
        if (e.target.closest('[data-cx-addcart]')) { self.addToCart(); return; }

        if (e.target.closest('[data-cx-poolinfo]')) { self.dialog(true); return; }
        if (e.target.closest('[data-cx-dialogclose]')) { self.dialog(false); return; }

        var trigger = e.target.closest('[data-pst-explain-open]');
        if (trigger) {
          var d = document.getElementById(trigger.getAttribute('data-pst-explain-open'));
          if (d && typeof d.showModal === 'function') { e.preventDefault(); d.showModal(); }
          return;
        }
        var closer = e.target.closest('[data-pst-explain-close]');
        if (closer) {
          var dc = closer.closest('dialog');
          if (dc) dc.close();
          return;
        }
      });

      /* Where the click STARTED has to be recorded before anything re-renders.
         Removing a line rebuilds the rail, so by the time the click event
         reaches document the button that was clicked is detached — and a
         detached node reports no ancestors, so an inside-the-panel click looked
         like an outside one and slammed the panel shut on every Remove. */
      document.addEventListener(
        'pointerdown',
        function (e) {
          self._fromTotal = !!(e.target.closest && e.target.closest('[data-cx-totalpanel], [data-cx-totaltoggle]'));
        },
        true
      );
      document.addEventListener('click', function () {
        var panel = self.querySelector('[data-cx-totalpanel]');
        if (!panel || panel.hidden) return;
        if (self._fromTotal) return;
        self.toggleTotal(false);
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') self.toggleTotal(false);
      });
    }

    /* ---- a pattern's fit choices only appear when it genuinely has more than one ---- */
    syncLinerFits() {
      var pattern = this.patternValue();
      Array.prototype.forEach.call(this.querySelectorAll('[data-cx-fit]'), function (block) {
        var mine = block.getAttribute('data-cx-fit') === pattern;
        var many = parseInt(block.getAttribute('data-count') || '1', 10) > 1;
        block.hidden = !mine || !many;

        if (!mine) return;
        var rows = Array.prototype.slice.call(block.querySelectorAll('[data-cx-liner] .pst-cx-opt__input'));
        if (!rows.length) return;
        // With one build there is nothing to ask, so choose it silently. With
        // several, land on the cheapest so the headline "from" price holds.
        if (!rows.some(function (r) { return r.checked; })) {
          var cheapest = rows.reduce(function (best, r) {
            var row = r.closest('[data-cx-liner]');
            var p = parseInt(row.getAttribute('data-price') || '0', 10);
            // On a tie (two builds both free) land on the pool's own included liner.
            var tieWin = best && p === best.price && row.getAttribute('data-included') === 'true';
            return !best || p < best.price || tieWin ? { input: r, price: p } : best;
          }, null);
            if (cheapest) cheapest.input.checked = true;
        }
      });
    }

    /* ============================================================
       The stage — one big picture of the thing just chosen
       ============================================================ */
    setStage(html, kicker, name) {
      if (!this.stageFrame) return;
      var self = this;
      var apply = function () {
        self.stageFrame.innerHTML = html;
        var k = self.querySelector('[data-cx-stage-kicker]');
        var n = self.querySelector('[data-cx-stage-name]');
        if (k) k.textContent = kicker;
        if (n) n.textContent = name;
        self.fitStage();
      };
      if (!REDUCED && document.startViewTransition) {
        try { document.startViewTransition(apply); return; } catch (e) {}
      }
      apply();
      if (!REDUCED) {
        this.stageFrame.classList.remove('is-swapping');
        void this.stageFrame.offsetWidth;
        this.stageFrame.classList.add('is-swapping');
      }
    }

    /* Hand the frame the picture's real aspect ratio so it can hug it. Has to
       wait for the decode when the image is not cached — naturalWidth is 0
       until then, and a ratio of 0 would collapse the frame. */
    fitStage() {
      var frame = this.stageFrame;
      if (!frame) return;
      var img = frame.querySelector('img');
      if (!img) { frame.style.removeProperty('--cx-stage-ar'); return; }
      var apply = function () {
        if (img.naturalWidth && img.naturalHeight) {
          frame.style.setProperty('--cx-stage-ar', img.naturalWidth + ' / ' + img.naturalHeight);
        }
      };
      if (img.complete) apply();
      else img.addEventListener('load', apply, { once: true });
    }

    mediaHTML(el) {
      var media = el.querySelector('.pst-cx-opt__media');
      if (media) return media.innerHTML;
      var full = el.getAttribute('data-image');
      if (full) return '<img src="' + esc(full) + '" alt="">';
      return this.poolStageHTML;
    }

    stageFrom(el) {
      var group = el.closest('[data-cx-group]');
      var kicker = group ? (group.querySelector('.pst-cx-grp__title') || {}).textContent : '';
      var name = el.getAttribute('data-summary') || el.getAttribute('data-title') || '';
      if (!name) {
        var n = el.querySelector('.pst-cx-opt__name, .pst-cx-fit__name');
        name = n ? n.textContent : '';
      }
      if (el.getAttribute('data-cx-none') === 'true') { this.stageDefault(); return; }
      this.setStage(this.mediaHTML(el), (kicker || 'Your choice').trim(), name.trim());
    }

    stageFromLiner(swatch) {
      var l = this.liner();
      if (l && l.el) { this.setStage((this.mediaHTML(l.el) || this.mediaHTML(swatch)) + this.freeTag(l.el), this.linerKicker(l.el), l.summary); return; }
      var name = swatch.querySelector('.pst-cx-opt__name');
      this.setStage(this.mediaHTML(swatch) + this.freeTag(swatch), this.linerKicker(swatch), name ? name.textContent : '');
    }

    /* FREE Liner program: the pool's own included liner (a liner row or pattern
       swatch carrying data-included) gets a "FREE with your pool" tag on the stage. */
    isIncluded(el) {
      return this.freeLiner && !!el && el.getAttribute('data-included') === 'true';
    }
    freeTag(el) {
      return this.isIncluded(el)
        ? '<span class="pst-cx__stage-free" aria-hidden="true"><b>FREE</b><span>with your pool</span></span>'
        : '';
    }
    linerKicker(el) { return this.isIncluded(el) ? 'Your liner · on us' : 'Your liner'; }

    stagePool() { this.setStage(this.poolStageHTML, 'Your pool', this.pool.title + ' above-ground pool'); }

    /* what the stage shows when a step opens and nothing in it was just clicked */
    stageDefault() {
      var key = this.steps[this.step] ? this.steps[this.step].getAttribute('data-cx-step') : '';
      if (key === 'liner') {
        var l = this.liner();
        if (l && l.el) { this.setStage(this.mediaHTML(l.el) + this.freeTag(l.el), this.linerKicker(l.el), l.summary); return; }
        var checkedSwatch = this.querySelector('[data-cx-pattern] .pst-cx-opt__input:checked');
        if (checkedSwatch) { this.stageFromLiner(checkedSwatch.closest('[data-cx-pattern]')); return; }
      } else if (key === 'gear' || key === 'extras') {
        var step = this.steps[this.step];
        var chosen = this.picks().filter(function (p) { return step.contains(p.el); });
        if (chosen.length) { this.stageFrom(chosen[chosen.length - 1].el); return; }
      }
      this.stagePool();
    }

    /* ============================================================
       Step navigation
       ============================================================ */
    buildNav() {
      var nav = this.querySelector('[data-cx-nav]');
      if (!nav) return;
      var html = '';
      this.steps.forEach(function (s, i) {
        html += '<button type="button" class="pst-cx__tab" data-cx-tab="' + i + '">'
          + '<span class="pst-cx__tab-num">' + (i + 1) + '</span>'
          + '<span class="pst-cx__tab-label">' + esc(s.getAttribute('data-cx-label') || ('Step ' + (i + 1))) + '</span>'
          + '</button>';
      });
      nav.innerHTML = html;
      this.tabs = Array.prototype.slice.call(nav.querySelectorAll('[data-cx-tab]'));
    }

    goKey(key) {
      for (var i = 0; i < this.steps.length; i++) {
        if (this.steps[i].getAttribute('data-cx-step') === key) { this.go(i, true); return; }
      }
    }

    go(index, scroll) {
      this.step = Math.max(0, Math.min(this.steps.length - 1, index));
      var self = this;

      this.steps.forEach(function (s, i) {
        var on = i === self.step;
        s.hidden = !on;
        if (on) s.setAttribute('data-cx-on', ''); else s.removeAttribute('data-cx-on');
      });

      var num = this.steps[this.step].querySelector('[data-cx-stepnum]');
      if (num) num.textContent = String(this.step + 1);

      this.stageDefault();
      this.refresh();
      this.persist();

      // The panel scrolls inside itself now, so a new step opens at its own
      // heading. Lifting the page past the site header at the same time hands
      // the whole viewport over on the first Next.
      if (scroll) {
        var panel = this.querySelector('.pst-cx__panel');
        if (panel) panel.scrollTo({ top: 0, behavior: REDUCED ? 'auto' : 'smooth' });
        this.clearHeader();
      }
    }

    /* The site header is the page's only scrollable content on this template.
       Scrolling it away once leaves the configurator filling the screen.
       `instant` on page open, where a glide down would just look like a jump. */
    clearHeader(instant) {
      var section = this.closest('.pst-cx-section') || this.parentElement;
      if (!section) return;
      var top = section.getBoundingClientRect().top + window.pageYOffset;
      if (window.pageYOffset >= top - 1) return;
      window.scrollTo({ top: top, behavior: instant ? 'instant' : (REDUCED ? 'auto' : 'smooth') });
    }

    next() {
      if (this.step >= this.steps.length - 1) { this.addToCart(); return; }
      this.go(this.step + 1, true);
    }

    /* Drives the green tick in the tab bar. The "I don't need this" cards are
       checked by default, so "every group has something checked" would tick every
       step before the shopper had seen it. A step counts as done once they have
       actually picked something in it, or once they have moved past it. */
    done(index) {
      var step = this.steps[index];
      if (!step) return false;
      var key = step.getAttribute('data-cx-step');
      if (key === 'summary') return false;
      if (key === 'liner') return !!this.liner() || this.ownsLiner();
      var picked = this.picks().some(function (p) { return step.contains(p.el); });
      return picked || index < this.step;
    }

    /* ============================================================
       Render everything that reflects the current build
       ============================================================ */
    refresh() {
      var self = this;
      var totalCents = this.total();
      var liner = this.liner();
      var picks = this.picks();

      // running total (mirrored into the bar and the summary)
      var text = money(totalCents, this.currency);
      Array.prototype.forEach.call(this.querySelectorAll('[data-cx-total]'), function (el) {
        if (el.textContent !== text) {
          el.textContent = text;
          if (el.classList.contains('pst-cx__total-amt')) {
            el.classList.remove('is-bump');
            void el.offsetWidth;
            el.classList.add('is-bump');
          }
        }
      });

      var n = this.count();
      this.setAll('[data-cx-count]', n + (n === 1 ? ' item' : ' items'));

      // tabs
      if (this.tabs) {
        this.tabs.forEach(function (tab, i) {
          var on = i === self.step;
          if (on) tab.setAttribute('aria-current', 'step'); else tab.removeAttribute('aria-current');
          tab.setAttribute('data-done', String(self.done(i)));
        });
      }
      var fill = this.querySelector('[data-cx-progress]');
      if (fill) fill.style.width = ((this.step + 1) / this.steps.length * 100) + '%';

      // footer
      var last = this.step === this.steps.length - 1;
      var nextLabel = this.querySelector('[data-cx-nextlabel]');
      if (nextLabel) {
        nextLabel.textContent = last
          ? 'Add to cart'
          : 'Next: ' + (this.steps[this.step + 1].getAttribute('data-cx-label') || 'Continue');
      }
      var prev = this.querySelector('[data-cx-prev]');
      if (prev) prev.hidden = this.step === 0;
      var status = this.querySelector('[data-cx-status]');
      if (status) status.textContent = this.statusLine(liner);

      this.renderRows(liner, picks);
      this.renderMini(liner, picks);
    }

    statusLine(liner) {
      var key = this.steps[this.step].getAttribute('data-cx-step');
      if (key === 'liner') {
        if (liner && this.freeLiner) return 'Liner: ' + liner.summary + (liner.price > 0 ? ' · +' + money(liner.price, this.currency) + ' upgrade' : ' · FREE with your pool');
        if (liner) return 'Liner: ' + liner.summary;
        return this.ownsLiner() ? "You'll use your own liner" : 'Your pool needs a liner to hold water';
      }
      var step = this.steps[this.step];
      var mine = this.picks().filter(function (p) { return step.contains(p.el); });
      if (key === 'summary') return 'Everything ships together';
      if (!mine.length) return 'Nothing added yet — that’s fine, you can skip this';
      return mine.length + (mine.length === 1 ? ' item added' : ' items added');
    }

    /* FREE Liner rows (data-free-liner) read "FREE $x̶" or "+$x" — their data-price is already
       what the shopper pays, so totals need no special case; only the label does. */
    priceLabel(item, cents) {
      var el = item && item.el;
      if (el && el.hasAttribute && el.hasAttribute('data-free-liner')) {
        if (cents <= 0) {
          var full = parseInt(el.getAttribute('data-full-price') || '0', 10);
          return '<span class="pst-cx-free">FREE</span>' + (full > 0 ? ' <s>' + money(full, this.currency) + '</s>' : '');
        }
        return '+' + money(cents, this.currency);
      }
      return money(cents, this.currency);
    }

    rowHTML(item, kicker, stepKey, removeId) {
      var qty = item.qty || 1;
      var art = item.thumb
        ? '<img src="' + esc(item.thumb) + '" alt="" loading="lazy">'
        : this.mediaHTML(item.el);
      return '<div class="pst-cx-sum__row">'
        + '<span class="pst-cx-sum__thumb">' + art + '</span>'
        + '<span class="pst-cx-sum__body">'
        + '<span class="pst-cx-sum__kicker">' + esc(kicker) + '</span>'
        + '<span class="pst-cx-sum__name">' + esc(item.title) + '</span>'
        + (qty > 1 ? '<span class="pst-cx-sum__meta">' + qty + ' × ' + money(item.price, this.currency) + '</span>' : '')
        + '<span class="pst-cx-sum__actions">'
        + '<button type="button" class="pst-cx-sum__edit" data-cx-goto="' + esc(stepKey) + '">Change</button>'
        + (removeId ? '<button type="button" class="pst-cx-sum__remove" data-cx-remove="' + esc(removeId) + '">Remove</button>' : '')
        + '</span>'
        + '</span>'
        + '<span class="pst-cx-sum__price">' + this.priceLabel(item, item.price * qty) + '</span>'
        + '</div>';
    }

    renderRows(liner, picks) {
      var wrap = this.querySelector('[data-cx-sum-rows]');
      if (!wrap) return;
      var self = this;
      var html = '';

      // Under the FREE Liner program a liner is required: it can be changed, not removed.
      if (liner) html += this.rowHTML(liner, 'Liner', 'liner', this.freeLiner ? null : 'liner');

      picks.forEach(function (p) {
        var group = p.el.closest('[data-cx-group]');
        var kicker = group ? (group.querySelector('.pst-cx-grp__title') || {}).textContent : 'Added';
        var stepEl = p.el.closest('[data-cx-step]');
        var stepKey = stepEl ? stepEl.getAttribute('data-cx-step') : 'extras';
        html += self.rowHTML(p, (kicker || 'Added').trim(), stepKey, p.id);
      });

      wrap.innerHTML = html;
      var none = this.querySelector('[data-cx-sum-none]');
      if (none) none.hidden = !!liner || picks.length > 0;
    }

    /* A line per item with its picture and a Remove, the way the builder's
       "Your kit" rail worked. The pool is the one row without a Remove — it is
       what the page is for. */
    kitRowHTML(item, meta, removeId) {
      var art = item.thumb
        ? '<img src="' + esc(item.thumb) + '" alt="">'
        : (item.el ? this.mediaHTML(item.el) : this.poolStageHTML);
      return '<div class="pst-cx__kitrow">'
        + '<span class="pst-cx__kitthumb">' + art + '</span>'
        + '<span class="pst-cx__kitbody">'
        + '<span class="pst-cx__kittitle">' + esc(item.title) + '</span>'
        + (meta ? '<span class="pst-cx__kitmeta">' + esc(meta) + '</span>' : '')
        + (removeId ? '<button type="button" class="pst-cx__kitremove" data-cx-remove="' + esc(removeId) + '">Remove</button>' : '')
        + '</span>'
        + '<span class="pst-cx__kitprice">' + this.priceLabel(item, item.price) + '</span>'
        + '</div>';
    }

    renderMini(liner, picks) {
      var wrap = this.querySelector('[data-cx-minilist]');
      if (!wrap) return;
      var self = this;
      var html = this.kitRowHTML(
        { title: this.pool.title + ' pool', price: this.pool.price, thumb: this.pool.thumb },
        'Your pool',
        null
      );
      if (liner) {
        html += this.kitRowHTML(
          { title: liner.title, price: liner.price, thumb: liner.thumb, el: liner.el },
          liner.summary,
          this.freeLiner ? null : 'liner'
        );
      }
      picks.forEach(function (p) {
        var group = p.el.closest('[data-cx-group]');
        var kicker = group ? (group.querySelector('.pst-cx-grp__title') || {}).textContent : '';
        var meta = (kicker || '').trim();
        if (p.qty > 1) meta = p.qty + ' × ' + money(p.price, self.currency) + (meta ? ' · ' + meta : '');
        html += self.kitRowHTML({ title: p.title, price: p.price * p.qty, thumb: p.thumb, el: p.el }, meta, p.id);
      });
      wrap.innerHTML = html;
    }

    /* Un-tick whatever produced this line. For a "pick one" group that means
       falling back to its "I don't need this" card rather than leaving the
       group with nothing selected, so the group still reads as answered. */
    removeItem(id) {
      if (id === 'liner') {
        var own = this.querySelector('[data-cx-pattern-none] .pst-cx-opt__input');
        if (own) own.checked = true;
        else if (this.freeLiner && this.defaultPattern) this.preselectLiner(this.defaultPattern); // required: fall back to the free one
        this.syncLinerFits();
      } else {
        var card = this.querySelector('[data-cx-opt][data-id="' + CSS.escape(id) + '"]');
        if (!card) return;
        var input = card.querySelector('.pst-cx-opt__input');
        if (input) input.checked = false;
        if (card.getAttribute('data-mode') === 'one') {
          var group = card.closest('[data-cx-group]');
          var none = group && group.querySelector('[data-cx-none="true"] .pst-cx-opt__input');
          if (none) none.checked = true;
        }
      }
      this.stageDefault();
      this.refresh();
      this.persist();
    }

    toggleTotal(force) {
      var panel = this.querySelector('[data-cx-totalpanel]');
      var btn = this.querySelector('[data-cx-totaltoggle]');
      if (!panel) return;
      var open = typeof force === 'boolean' ? force : panel.hidden;
      panel.hidden = !open;
      if (btn) btn.setAttribute('aria-expanded', String(open));
    }

    dialog(open) {
      var d = this.querySelector('[data-cx-pooldialog]');
      if (!d) return;
      if (open && typeof d.showModal === 'function') d.showModal();
      else if (typeof d.close === 'function') d.close();
    }

    /* ============================================================
       Add the whole package in one POST, then open the theme's drawer
       ============================================================ */
    addToCart() {
      var self = this;
      var items = [{ id: this.pool.variantId, quantity: 1 }];
      var liner = this.liner();
      if (liner && liner.variantId) items.push({ id: liner.variantId, quantity: 1 });
      this.picks().forEach(function (p) {
        if (p.variantId) items.push({ id: p.variantId, quantity: p.qty });
      });
      if (!items[0].id) return;

      var cta = this.querySelector('[data-cx-addcart]') || this.querySelector('[data-cx-next]');
      if (cta) cta.setAttribute('aria-busy', 'true');

      var drawer = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (drawer && typeof drawer.setActiveElement === 'function' && cta) drawer.setActiveElement(cta);

      var payload = { items: items };
      var sections = this.cartSections(drawer);
      if (sections.length) {
        payload.sections = sections.join(',');
        payload.sections_url = window.location.pathname;
      }

      fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload)
      })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          if (res.status) { self.error(res.description); return; }
          if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
              source: 'pst-pool-customizer',
              productVariantId: items[0].id
            });
          }
          if (drawer && typeof drawer.renderContents === 'function') drawer.renderContents(res);
          else window.location.href = (window.routes && window.routes.cart_url) || '/cart';
        })
        .catch(function () { window.location.href = (window.routes && window.routes.cart_url) || '/cart'; })
        .finally(function () {
          if (cta) cta.removeAttribute('aria-busy');
          // Dawn's renderContents only strips `is-empty` from `.drawer__inner`;
          // <cart-drawer> itself keeps the class it rendered with, so after the
          // first add to an empty cart the list stays hidden until reload. The
          // theme's own product-form clears it here — mirror that exactly.
          if (drawer && drawer.classList.contains('is-empty')) drawer.classList.remove('is-empty');
        });
    }

    cartSections(drawer) {
      var ids = [];
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        try {
          drawer.getSectionsToRender().forEach(function (s) { if (s.id) ids.push(s.id); });
        } catch (e) {}
      }
      return ids.length ? ids : ['cart-drawer', 'cart-icon-bubble'];
    }

    error(msg) {
      var box = this.querySelector('[data-cx-error]');
      var text = msg || (window.cartStrings && window.cartStrings.error) || 'Sorry — we could not add that to the cart.';
      if (box) {
        box.textContent = text;
        box.hidden = false;
        box.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'center' });
      } else {
        window.alert(text);
      }
    }

    setAll(sel, text) {
      Array.prototype.forEach.call(this.querySelectorAll(sel), function (el) { el.textContent = text; });
    }
  }

  if (!customElements.get('pst-pool-customizer')) {
    customElements.define('pst-pool-customizer', PstPoolCustomizer);
  }
})();
